import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Clock, Eye, Building2, FileText,
  Download, User, Mail, Phone, MapPin, Globe, Calendar, Image as ImageIcon
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

const docLabels: Record<string, string> = {
  business_registration_url: 'Business Registration',
  trade_license_url: 'Trade License',
  tin_certificate_url: 'TIN Certificate',
  vat_certificate_url: 'VAT Certificate',
  developer_license_url: 'Developer License',
  involvement_letter_url: 'Involvement Letter',
  company_profile_url: 'Company Profile',
  id_document_url: 'ID Document (Fayda)',
  power_of_attorney_url: 'Power of Attorney',
  membership_fee_proof_url: 'Membership Fee Proof',
  recommendation_letter_url: 'Recommendation Letter',
  financial_statement_url: 'Financial Statement',
  tax_clearance_url: 'Tax Clearance',
};

export function DeveloperApplications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { data: applications, isLoading } = useQuery({
    queryKey: ['developer-applications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('developer_applications' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: profiles } = useQuery({
    queryKey: ['app-profiles', applications?.map((a: any) => a.user_id)],
    queryFn: async () => {
      if (!applications?.length) return {};
      const userIds = applications.map((a: any) => a.user_id);
      const { data } = await supabase.from('profiles').select('*').in('user_id', userIds);
      return Object.fromEntries((data || []).map((p: any) => [p.user_id, p]));
    },
    enabled: !!applications?.length,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ appId, status, notes }: { appId: string; status: string; notes: string }) => {
      const { error: updateError } = await supabase
        .from('developer_applications' as any)
        .update({ status, reviewer_notes: notes, reviewed_by: user!.id, reviewed_at: new Date().toISOString() })
        .eq('id', appId);
      if (updateError) throw updateError;

      const app = applications?.find((a: any) => a.id === appId);
      if (!app) throw new Error('Application not found');

      if (status === 'approved') {
        const { error: devErr } = await supabase.from('developer_profiles').insert({
          user_id: app.user_id, company_name: app.company_name, phone: app.phone,
          email: app.email, address: app.address, city: app.city, website: app.website,
          established_year: app.established_year,
          description: app.description ? { en: app.description, am: '', om: '' } : null,
        });
        if (devErr && !devErr.message.includes('duplicate')) throw devErr;
        const { error: roleErr } = await supabase.from('user_roles').insert({ user_id: app.user_id, role: 'developer' as any });
        if (roleErr && !roleErr.message.includes('duplicate')) throw roleErr;
      }

      // Notify applicant
      await supabase.from('notifications').insert({
        user_id: app.user_id,
        title: status === 'approved' ? 'Your developer application was approved' : 'Your developer application was rejected',
        message: notes || (status === 'approved' ? 'Welcome aboard! You can now manage your projects.' : 'Please review and resubmit.'),
        type: status === 'approved' ? 'application_approved' : 'application_rejected',
        metadata: { application_id: app.id, application_type: 'developer' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer-applications'] });
      toast.success(actionType === 'approve' ? 'Application approved! Developer profile created and applicant notified.' : 'Application rejected. Applicant notified.');
      setSelectedApp(null);
      setActionType(null);
      setReviewNotes('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const getDocUrl = async (path: string) => {
    const { data } = await supabase.storage.from('developer-documents').createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  const viewDocImage = async (path: string) => {
    const { data } = await supabase.storage.from('developer-documents').createSignedUrl(path, 3600);
    if (data?.signedUrl) setPreviewImage(data.signedUrl);
  };

  const pendingCount = applications?.filter((a: any) => a.status === 'pending').length || 0;

  const renderInfoRow = (icon: React.ReactNode, label: string, value: string | number | null | undefined) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <div className="text-muted-foreground mt-0.5">{icon}</div>
        <div>
          <div className="text-xs text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-medium">{value}</div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Developer Applications</h2>
        {pendingCount > 0 && <Badge className="bg-yellow-500 text-white">{pendingCount} Pending</Badge>}
      </div>

      {!applications?.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No developer applications yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {applications.map((app: any) => {
            const cfg = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.pending;
            const profile = profiles?.[app.user_id];
            return (
              <Card key={app.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{app.company_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {profile?.full_name || 'Unknown'} • {app.city || 'N/A'} • {new Date(app.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge className={cfg.color}><cfg.icon className="h-3 w-3 mr-1" /> {cfg.label}</Badge>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedApp(app); setReviewNotes(''); }}>
                      <Eye className="h-4 w-4 mr-1" /> Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedApp} onOpenChange={() => { setSelectedApp(null); setActionType(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Developer Application: {selectedApp?.company_name}
            </DialogTitle>
            {selectedApp && (
              <div className="flex items-center gap-2 mt-1">
                <Badge className={statusConfig[selectedApp.status as keyof typeof statusConfig]?.color || ''}>
                  {statusConfig[selectedApp.status as keyof typeof statusConfig]?.label || selectedApp.status}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Submitted {new Date(selectedApp.created_at).toLocaleDateString()} at {new Date(selectedApp.created_at).toLocaleTimeString()}
                </span>
              </div>
            )}
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-5">
              {/* Company Info */}
              <div>
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" /> Company Information
                </h4>
                <div className="border rounded-xl p-4 bg-muted/30">
                  {renderInfoRow(<Building2 className="h-4 w-4" />, 'Company Name', selectedApp.company_name)}
                  {renderInfoRow(<Mail className="h-4 w-4" />, 'Email', selectedApp.email)}
                  {renderInfoRow(<Phone className="h-4 w-4" />, 'Phone', selectedApp.phone)}
                  {renderInfoRow(<MapPin className="h-4 w-4" />, 'Location', [selectedApp.city, selectedApp.address].filter(Boolean).join(', '))}
                  {renderInfoRow(<Globe className="h-4 w-4" />, 'Website', selectedApp.website)}
                  {renderInfoRow(<Calendar className="h-4 w-4" />, 'Established Year', selectedApp.established_year)}
                  {selectedApp.description && (
                    <div className="pt-2">
                      <div className="text-xs text-muted-foreground font-medium mb-1">Description</div>
                      <p className="text-sm bg-muted/50 p-3 rounded-lg">{selectedApp.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Applicant profile info */}
              {profiles?.[selectedApp.user_id] && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" /> Applicant Account
                    </h4>
                    <div className="border rounded-xl p-4 bg-muted/30">
                      {renderInfoRow(<User className="h-4 w-4" />, 'Full Name', profiles[selectedApp.user_id]?.full_name)}
                      {renderInfoRow(<Phone className="h-4 w-4" />, 'Phone', profiles[selectedApp.user_id]?.phone)}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Documents */}
              <div>
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" /> Uploaded Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(docLabels).map(([field, label]) => {
                    const hasDoc = !!selectedApp[field];
                    const isImage = field === 'id_document_url';
                    return (
                      <div key={field} className={`flex items-center justify-between p-3 rounded-lg border text-sm ${hasDoc ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-muted/50 border-dashed'}`}>
                        <div className="flex items-center gap-2">
                          {isImage ? <ImageIcon className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-muted-foreground'}`} /> :
                            <FileText className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-muted-foreground'}`} />}
                          <span className={hasDoc ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
                          {hasDoc && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                          {!hasDoc && <span className="text-xs text-red-400">Not uploaded</span>}
                        </div>
                        {hasDoc && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => viewDocImage(selectedApp[field])}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => getDocUrl(selectedApp[field])}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  {Object.keys(docLabels).filter(f => !!selectedApp[f]).length} of {Object.keys(docLabels).length} documents uploaded
                </div>
              </div>

              <Separator />

              {/* Previous review notes */}
              {selectedApp.reviewer_notes && (
                <div className="p-4 bg-muted rounded-xl">
                  <div className="text-xs font-bold mb-1">Previous Review Notes</div>
                  <p className="text-sm">{selectedApp.reviewer_notes}</p>
                  {selectedApp.reviewed_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Reviewed on {new Date(selectedApp.reviewed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Action area */}
              {selectedApp.status === 'pending' && (
                <div className="space-y-3 pt-2">
                  <Textarea
                    placeholder="Add review notes — explain the reason if rejecting..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={3}
                  />
                  <DialogFooter className="gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => { setActionType('reject'); reviewMutation.mutate({ appId: selectedApp.id, status: 'rejected', notes: reviewNotes }); }}
                      disabled={reviewMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button
                      onClick={() => { setActionType('approve'); reviewMutation.mutate({ appId: selectedApp.id, status: 'approved', notes: reviewNotes }); }}
                      disabled={reviewMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Create Profile
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Document Preview</DialogTitle></DialogHeader>
          {previewImage && <img src={previewImage} alt="Document preview" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
